import { useMemo } from "react";

import { Classes, Colors } from "@blueprintjs/core";
import styled from "@emotion/styled";
import classnames from "classnames";
import { Link, useLocation, useNavigate, useParams } from "react-router";

import { FEATURE_CONFIGS } from "@/components/helpers/configuration/constants";
import {
  enableJobSearch,
  enableReviews,
} from "@/components/helpers/configuration/utils";
import { Box, Flex } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import { Filter } from "@/components/pieces/filter/Filter";
import { FilterButton } from "@/components/pieces/filter/FilterButton";
import { BRAND_PURPLE, STANDARD_NAV_HEIGHT } from "@/css/constants";
import {
  CustomerUser,
  StaffReviewsUser,
  StaffSessionsUser,
} from "@/types/users";

interface NavLink {
  label: string;
  to: string;
  otherMatches?: string[];
  configType?: string;
}

const COACH_LINKS: NavLink[] = [
  { label: "Profile", to: "/client/:userId", otherMatches: [] },
  {
    label: "Sessions",
    to: "/client/:userId/sessions",
    otherMatches: [],
  },
  {
    label: "Reviews",
    to: "/client/:userId/reviews",
    configType: FEATURE_CONFIGS.enableReviews,
    otherMatches: [],
  },
  {
    label: "Messages",
    to: "/client/:userId/messages",
    otherMatches: ["/client/:userId/messages/:channelId"],
  },
];

interface VerifiedNavLink extends NavLink {
  active?: boolean;
  url: string;
}

const useVerifiedLinks = (
  links: NavLink[],
  clientId?: string,
): VerifiedNavLink[] => {
  const { userId, channelId, slug } = useParams();
  const location = useLocation();

  const replaceContext: Record<string, string> = {
    ":userId": userId || clientId || "",
    ":channelId": channelId || "",
    ":slug": slug || "",
  };

  const matchedPath = location.pathname;

  const verifiedLinks: VerifiedNavLink[] = links.map((link) => {
    const candidateUrls = [link.to].concat(link.otherMatches || []);
    const verifiedUrls = candidateUrls.map((url) =>
      url.replace(
        /(:userId|:channelId|:slug)/g,
        (match) => replaceContext[match],
      ),
    );
    const active = verifiedUrls.includes(matchedPath);
    return {
      ...link,
      active,
      url: verifiedUrls[0],
    };
  });

  return verifiedLinks;
};

interface LinksProps {
  client?: StaffSessionsUser | CustomerUser | StaffReviewsUser | null;
}

export function Links({ client }: LinksProps) {
  const loading = !client;
  const navigate = useNavigate();
  const { userId: paramUserId } = useParams();

  const userId = paramUserId || client?.id;

  const disableJobSearch = useMemo(() => !enableJobSearch(client), [client]);
  const disableReviews = useMemo(() => !enableReviews(client), [client]);

  const links = COACH_LINKS;

  const configFilteredLinks = links.filter((link) => {
    return (
      !(
        link.configType === FEATURE_CONFIGS.enableJobSearch && disableJobSearch
      ) &&
      !(link.configType === FEATURE_CONFIGS.enableReviews && disableReviews)
    );
  });

  const verifiedLinks = useVerifiedLinks(configFilteredLinks, userId);
  const activeLink =
    verifiedLinks.find((link) => link.active) || verifiedLinks[0];

  const linksOptions = configFilteredLinks.map((link) => ({
    id: link.to,
    name: link.label,
  }));

  return (
    <>
      <Media greaterThan="xs">
        <Flex alignItems="center">
          {verifiedLinks.map((link) => {
            const cls = classnames({
              [Classes.SKELETON]: loading,
              active: !loading && link.active,
            });
            return (
              <MenuLinkContainer key={link.label} className={cls} to={link.url}>
                {link.label}
              </MenuLinkContainer>
            );
          })}
        </Flex>
      </Media>
      <Media at="xs">
        <Box py={3}>
          <Filter
            button={
              <FilterButton rightIcon="chevron-down" text={activeLink.label} />
            }
            options={linksOptions}
            selectedOptions={linksOptions.filter(
              (link) => link.id === activeLink.to,
            )}
            onFilterChange={(x) => {
              if (x.id !== activeLink.to && userId) {
                navigate(x.id.replace(":userId", userId));
              }
            }}
          />
        </Box>
      </Media>
    </>
  );
}

const MenuLinkContainer = styled(Link)`
  display: flex;
  transition: all 200ms ease;
  margin: 0 0 0 20px;
  padding: 0 2px;
  height: ${STANDARD_NAV_HEIGHT}px;
  align-items: center;
  border-top: 3px solid transparent;
  border-bottom: 3px solid transparent;
  transition: all 200ms ease;
  color: ${Colors.GRAY3};
  &:hover {
    border-bottom: 3px solid ${Colors.GRAY3};
    color: ${Colors.GRAY1};
  }
  &.active {
    color: ${Colors.BLACK};
    border-bottom: 3px solid ${BRAND_PURPLE} !important;
  }
  &.${Classes.SKELETON} {
    height: unset;
    border: none;
  }
`;
